import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, Coffee, Zap } from 'lucide-react';

export default function BlogsPage() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  const blogs = [
    {
      id: 'stop-wasting-time-marketing',
      title: 'Stop Wasting Time on Manual Marketing: Here\'s What Actually Works',
      excerpt: 'Spending hours every week trying to figure out what to post? Discover how a structured 30-day AI marketing plan lets small businesses stay consistent and professional without the guesswork.',
      category: 'Marketing Tips',
      readTime: '5 min read',
      publishDate: '29 December 2024',
      author: 'Digital Trent Team',
      icon: Zap,
      gridColor: '#3b82f6'
    }
  ];

  return (
    <div className="bg-slate-900 text-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-4 px-4 pt-24" data-animate="hero">
        <div className="max-w-5xl mx-auto">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold tracking-wider mb-6 rounded">
              <Coffee size={14} />
              BLOG & INSIGHTS
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-tight text-white">
              Real Talk About Modern Marketing
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed max-w-3xl">
              Practical insights, honest advice, and proven strategies from the trenches of AI-powered marketing. No fluff, just what works.
            </p>
          </div>
        </div>
      </section>

      {/* Blogs Grid */}
      <section className="py-8 px-4 bg-slate-900" data-animate="blogs">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {blogs.map((blog, index) => (
              <div
                key={blog.id}
                className={`group bg-slate-800/90 backdrop-blur-sm rounded-lg md:rounded-xl overflow-hidden border border-slate-700/60 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 ${
                  visibleSections.has('blogs') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                data-animate="blogs"
              >
                {/* Micro Grid */}
                <div className="absolute top-0 left-0 w-20 md:w-24 h-20 md:h-24 pointer-events-none opacity-30 overflow-hidden rounded-tl-lg">
                  <div
                    style={{
                      backgroundImage: `linear-gradient(${blog.gridColor} 0.5px, transparent 0.5px),
                                        linear-gradient(90deg, ${blog.gridColor} 0.5px, transparent 0.5px)`,
                      backgroundSize: '8px 8px',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>

                {/* Content */}
                <div className="p-6 relative">
                  {/* Icon & Category */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-slate-700/50 rounded-lg flex items-center justify-center">
                      <blog.icon size={20} className="text-blue-400" />
                    </div>
                    <span className="px-2.5 py-1 bg-blue-600/20 text-blue-400 text-xs font-bold tracking-wider rounded border border-blue-500/30">
                      {blog.category}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-lg md:text-xl font-bold mb-2 text-white group-hover:text-blue-400 transition-colors duration-300 leading-tight">
                    {blog.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">
                    {blog.excerpt}
                  </p>

                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-4 pb-4 border-b border-slate-700/50">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      <span>{blog.publishDate}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{blog.readTime}</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{blog.author}</span>
                    <Link
                      to={`/resources/blogs/${blog.id}`}
                      className="group/btn inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-medium transition-all duration-300 hover:gap-3"
                    >
                      Read More
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-12 px-4" data-animate="cta">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`transition-all duration-1000 ease-out ${
            visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-slate-900">
              Ready to Stop Wasting Time?
            </h2>
            <p className="text-base text-slate-600 mb-6 max-w-2xl mx-auto leading-relaxed">
              See how Digital Trent plans, designs, and publishes your 30-day marketing campaign — all in one platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/auth/signup')}
                className="group bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 inline-flex items-center justify-center gap-3"
              >
                Start Free Trial
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/company/contact')}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
